import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RATING_KIND_DEFS, type RatingItem, type RatingKind } from '@goyone/shared';
import { api } from '../../lib/api';
import { Icon } from '../../components/Icon';
import { PageHead } from '../../components/ui';
import { useToast } from '../../components/Toast';

/**
 * ضبط عناصر تقييم العملاء.
 *
 * لكل سياقٍ قائمتُه: ما يُسأل عنه العميل بعد مكالمةٍ (سرعة الرد، أسلوب
 * الموظف…) غيرُ ما يُسأل عنه عند تسليم مشروع (الالتزام بالوقت، جودة
 * التسليم…). القائمة الفارغة تعني نجومًا كلّيةً وتعليقًا فقط — وهذا
 * الافتراضي، فالصفحة تضيف عمقًا لمن يريده ولا تفرضه.
 *
 * التعديل هنا يسري على الروابط المفتوحة فورًا: العنصر يظهر لمن يفتح
 * الرابط بعد الحفظ — والدرجات المحفوظة من قبل لا تُمسّ.
 */
export function RatingSettings() {
  const config = useQuery({
    queryKey: ['ratings', 'config'],
    queryFn: () => api<{ items: Record<string, RatingItem[]> }>('/ratings/config'),
  });

  return (
    <div className="page">
      <PageHead
        title="تقييم العملاء"
        subtitle="اضبط ما يُسأل عنه العميل في كل سياق — والفارغ نجومٌ وتعليق فقط"
      />

      {config.isPending && <div className="skeleton" style={{ height: 300 }} />}

      {config.data && (
        <div className="rating-cfg-grid">
          {RATING_KIND_DEFS.map((def) => (
            <KindEditor
              key={def.key}
              kind={def.key}
              label={def.label}
              hint={def.hint}
              saved={config.data.items[def.key] ?? []}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   محرّر سياقٍ واحد
   ========================================================================== */

function KindEditor({
  kind,
  label,
  hint,
  saved,
}: {
  kind: RatingKind;
  label: string;
  hint: string;
  saved: RatingItem[];
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [items, setItems] = useState<RatingItem[]>(saved);
  const [draft, setDraft] = useState('');

  /* المحفوظ قد يصل بعد أول رسم — أو يتغيّر من تبويب آخر. */
  useEffect(() => setItems(saved), [saved]);

  const dirty = JSON.stringify(items) !== JSON.stringify(saved);

  const save = useMutation({
    mutationFn: () => api(`/ratings/config/${kind}`, { method: 'PUT', body: { items } }),
    onSuccess: () => {
      toast(`حُفظت عناصر «${label}»`);
      void queryClient.invalidateQueries({ queryKey: ['ratings', 'config'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const add = () => {
    const text = draft.trim();
    if (text.length < 2 || items.length >= 10) return;
    /* المعرّف من الترتيب والزمن — ثابتٌ للعنصر لا يتبدّل بإعادة تسمية. */
    const id = `i${Date.now().toString(36)}${items.length}`;
    setItems((list) => [...list, { id, label: text }]);
    setDraft('');
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">{label}</h2>
        <span className="tiny mute">{hint}</span>
      </div>
      <div className="card-body">
        {items.length === 0 && (
          <p className="tiny mute mb-3">
            لا عناصر — صفحة التقييم تعرض نجومًا كلّيةً وتعليقًا فقط.
          </p>
        )}

        <div className="col" style={{ gap: 'var(--sp-2)' }}>
          {items.map((item, i) => (
            <div key={item.id} className="row" style={{ gap: 'var(--sp-2)' }}>
              <span className="tiny mute num">{i + 1}</span>
              <input
                className="input"
                value={item.label}
                maxLength={80}
                onChange={(e) =>
                  setItems((list) =>
                    list.map((x) => (x.id === item.id ? { ...x, label: e.target.value } : x)),
                  )
                }
              />
              <button
                className="btn btn-sm btn-icon btn-ghost"
                onClick={() => setItems((list) => list.filter((x) => x.id !== item.id))}
                title="حذف العنصر"
                aria-label={`حذف ${item.label}`}
              >
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>

        {items.length < 10 && (
          <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
            <input
              className="input"
              placeholder="عنصر جديد — مثل: سرعة الاستجابة"
              value={draft}
              maxLength={80}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
            />
            <button className="btn btn-sm" onClick={add} disabled={draft.trim().length < 2}>
              <Icon name="plus" size={14} />
              إضافة
            </button>
          </div>
        )}

        <div className="row mt-3">
          <button
            className="btn btn-primary btn-sm"
            disabled={!dirty || save.isPending || items.some((i) => i.label.trim().length < 2)}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'جارٍ الحفظ…' : 'حفظ'}
          </button>
          {dirty && <span className="tiny mute">تعديلات غير محفوظة</span>}
        </div>
      </div>
    </div>
  );
}
